import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, Subscription, of } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";
import { Location } from "src/app/admin/location/shared/models/location";
import { LocationRelationship } from "src/app/admin/location/shared/models/locationRelationship";
import { LocationService } from "src/app/admin/location/shared/services/location.service";
import { LocationSelectorInfo } from "src/app/admin/user/shared/models/locationSelectorModel";
import { UserService } from "src/app/admin/user/shared/services/user.service";
import { TenantModel } from "src/app/core/models/tenant";
import * as CentralLocationSelectorActions from "src/app/lobby/lobby-central-views/state/actions/lobby-central-view-location-selector.actions";
import { getIsImpersonateAssist } from "src/app/lobby/lobby-wrap-up/state";
import { LocationType } from "src/app/shared/enums/LocationType.enum";
import { UserSigninStatus } from "src/app/shared/enums/UserSigninStatus.enum";
import { LobbyLocationSelectorInfo } from "src/app/shared/models/lobby-location-selected.interface";
import { LocationSelectorItem } from "src/app/shared/models/location-selector/location-selector-item-model";
import { LocationSelector } from "src/app/shared/models/location-selector/location-selector-model";
import { LocationStateModel } from "src/app/shared/models/location-selector/location-state.model";
import { SessionService } from "src/app/shared/services/session.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { getShouldPromptUserToSignIn } from "src/app/state";
import { State } from "src/app/state/app.state";
import * as LobbyWrapUpPageActions from "../../../../../lobby/lobby-wrap-up/state/actions/wrap-up-page.actions";
import { UserSigninStatusModel } from "../../../../../lobby/shared/models/lobby-user-signin-status.model";
import * as CoreActions from "./../../../../../state/actions/core.actions";
import {
  getAvailableActiveBranches,
  getCurrentTenant,
  getLobbyLocationSelected,
  getLobbyShowCategory,
  getLoggedInUserId,
  getSignedInStatus,
  getUnableToCheckInPopupPrompt,
  getUnableToSignInPopupPrompt,
} from "./../../../../../state/index";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-signin-panel",
  templateUrl: "./app-signin-panel.component.html",
  styleUrls: ["./app-signin-panel.component.scss"],
})
export class AppSigninPanelComponent implements OnInit, OnDestroy {
  // Variables that manupulates the view
  public UserName: string = "";
  public IsSignedIn: boolean = false;
  public StatusString: string = "Signed Out";
  public ShowSignInPopup = false;
  public ShowUnableToSignInPopup = false;
  public SignOutLobby: any;
  public SignInLobby: any;

  // variables that are used to take inputs and bidirectional mappings
  public SignoutReason: string = "";

  // private variables non displayed
  private userId: number = 0;
  private locationId: number = 0;
  public locationName: string = "";
  private tenant?: TenantModel;
  private subs = new Subscription();
  public showImpersonateInfo: boolean = false;
  public impersonateByName: string = "";
  private intialLoad = true; // User should be prompted to sign in first time user loads the application
  private cachedSession?: UserSigninStatusModel;
  showLobbyCategory: boolean = false;
  ShowUnableToCheckInPopup: boolean = false;
  isImpersonateAssist: boolean = false;
  public locationHierachy: LocationSelector = {
    locations: [],
    locationsFlatData: [],
  };
  private activeBranches: LocationRelationship[] = [];
  public showUserAccountUpdatedPopup: boolean = false;
  public userAccountUpdateRows: { fieldKey: string; value: string }[] = [];

  constructor(
    private store: Store<State>,
    private session: SessionService,
    private locationService: LocationService,
    public userService: UserService,
    public toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.SignOutLobby = this.translateService.instant(
      "_Shared_App_SignIn_Out_Text_",
    );
    this.SignInLobby = this.translateService.instant(
      "_Shared_App_SignIn_In_Text_",
    );
    this.StatusString = this.translateService.instant(
      "_General_Signed_Out_Ts_Text_",
    );

    this.getActiveBranchesAvailable();
    if (this.intialLoad) {
      this.cachedSession = this.session.getSignInStatus();
    }
    this.subs.add(
      this.store.select(getLobbyShowCategory).subscribe((lobbyCategory) => {
        this.showLobbyCategory = lobbyCategory;
      }),
    );
    const impersonateFlagSubscription = this.store
      .select(getIsImpersonateAssist)
      .subscribe((flag) => {
        this.isImpersonateAssist = flag;
      });
    this.subs.add(impersonateFlagSubscription);
  }

  private _onReceiveTenant(tenant: TenantModel) {
    this.tenant = tenant;
    this._loadSigninDetails();
  }

  private _onReceiveUserId(userId: number): void {
    this.userId = userId;
  }

  private _onReceiveLocationId(location: LobbyLocationSelectorInfo): void {
    this.locationId = location.locationId;
    this.locationName = location.locationName;
    this._UpdatePanelView();
  }

  private _onReceiveSignInPopupPromptFlag(flag: boolean): void {
    if (!this.ShowUnableToSignInPopup) this.ShowSignInPopup = flag;
  }

  // Prompt the user signin popup
  private _ShowUserSigninPrompt(): void {
    if (!this.ShowUnableToSignInPopup)
      this.store.dispatch(
        CoreActions.promptUserToSignin({
          payload: { isSelectorChange: false },
        }),
      );
  }

  /**
   * Since we have more than two status in the backend This method would
   * FIXME Method too long
   * effectively convert them to signedin and signedout status And update
   * the panel view accordingly
   * @param status
   */
  private _onUserSignedInStatusUpdated(status: UserSigninStatusModel): void {
    if (
      status &&
      status.locationCode != null &&
      (status.statusId === UserSigninStatus.Idle ||
        status.statusId === UserSigninStatus.Assisting)
    ) {
      this.IsSignedIn = true;
      this.locationService
        .getLocationByCode(status.locationCode)
        .subscribe((response: Location) => {
          let dropDownDisabled = false;
          this.store
            .select(getLobbyLocationSelected)
            .pipe(take(1))
            .subscribe((location) => {
              dropDownDisabled = location.dropDownDisabled;
            });
          const locationSelectorInfo: LocationSelectorInfo = {
            id: response.id || 0,
            locationName: response.name,
            dropDownDisabled,
            locationCode: response.code,
            utcoffsetInMinutes: response.timeZone?.utcoffsetInMinutes,
            timezoneId: response.timeZone?.timeZoneId,
            timeZoneName: response.timeZone?.timeZoneName,
            locationTypeId: response.locationType?.id,
            lastLocationAutoWraupTime: response.lastLocationAutoWraupTime,
          };

          this.store.dispatch(
            CoreActions.updateLobbyLocationSelectorInfo({
              payload: {
                lobbyLocationSelectorInfo: locationSelectorInfo,
              },
            }),
          );
        });
      this.store.dispatch(
        CoreActions.loadLobbyLocationSelectorStatus({
          payload: {
            userId: this.userId,
            locationId: status.locationId,
            tenantId: this.tenant?.tenantID || 0,
          },
        }),
      );
      if (
        status.currentAssistingLobbyEntryId !== null &&
        status.currentAssistingLobbyEntryId > 0 &&
        status.impersonateById == null &&
        (status.impersonationTransferDetails?.length == 0 ||
          status.impersonationTransferDetails == null) &&
        !this.isImpersonateAssist
      ) {
        this.store.dispatch(
          LobbyWrapUpPageActions.assistLobbyEntry({
            lobbyEntryId: status.currentAssistingLobbyEntryId,
            assistingUserId: this.userId,
            isInstantAssist: true,
            locationId: this.locationId,
            showLobbyCategory: this.showLobbyCategory,
            isImpersonateAssist: false,
            impersonateByName: null,
          }),
        );
      } else if (!status.isImpersonateOtherUser) {
        this.store.dispatch(LobbyWrapUpPageActions.closeWrapUp());
      }
      if (status?.impersonateById != null) {
        this.showImpersonateInfo = true;
        if (status.impersonateByName) {
          this.impersonateByName = status.impersonateByName;
        }
      }
    } else {
      this.IsSignedIn = false;
    }

    if (this.intialLoad && status !== null) {
      if (!this.IsSignedIn) {
        this.userService
          .getJitUserNotifications(this.userId)
          .subscribe((value) => {
            if (value.status) {
              if (value.isUpdated) {
                this._openUserAccountUpdatedPopup(value?.message);
              } else {
                this.toastService.showSuccess(
                  "_Shared_App_Sigin_User_Account_Provision_Success_Text_",
                );
                this._ShowUserSigninPrompt();
              }
            } else this._ShowUserSigninPrompt();
          });
      }

      this.intialLoad = false;
    } else if (status !== null && !this.IsSignedIn) {
      this.store.dispatch(LobbyWrapUpPageActions.closeWrapUp());
    }

    this._UpdatePanelView();
  }

  // Update the variables to manupulate the view
  private _UpdatePanelView(): void {
    if (this.IsSignedIn) {
      this.translateService
        .get("_Shared_Signed_Into_Ts_Text_")
        .subscribe((value) => {
          this.StatusString = value + " " + this.locationName;
        });
    } else {
      this.translateService
        .get("_General_Signed_Out_Ts_Text_")
        .subscribe((value) => {
          this.StatusString = value;
        });
    }
    this.SignoutReason = "";
  }

  // Dispatch events to sign in the user and close the signin popup
  private _SignInUser(): void {
    this.store.dispatch(
      CoreActions.userSignIn({
        payload: {
          userId: this.userId,
          locationId: this.locationId,
          tenantId: this.tenant?.tenantID || 0,
        },
      }),
    );

    this._hidePopUp();
  }

  /**
   * Sets the default values for the central view.
   * @param loc - Array of LocationRelationship objects to filter by.
   * Fetches location hierarchy asynchronously and constructs an array of LocationStateModel.
   * Calls `_updateCentralLocationSelector` with the array.
   */
  private setCentralViewDefaultValue(loc: LocationRelationship[]) {
    this.locationService
      .getSelectorLocationHierachy(false, true, true, false, true)
      .subscribe({
        next: (locationsHierachy) => {
          const locationData = locationsHierachy as LocationSelector;
          const locationStateArray: LocationStateModel[] = [];
          if (loc.length !== 0) {
            locationData?.locationsFlatData.forEach((v) => {
              const locateStateObj: LocationStateModel = {
                locationId: v.locationId,
                locationName: v.locationName,
                locationTypeId: v.locationTypeId,
              };
              locationStateArray.push(locateStateObj);
            });
          }
          this._updateCentralLocationSelector(locationStateArray);
        },
        error: () => {},
      });
  }

  public CloseImpersonateInfo() {
    this.showImpersonateInfo = false;
  }

  private _updateCentralLocationSelector(locationStateArray: any) {
    this.store.dispatch(
      CentralLocationSelectorActions.setCentralLocationSelectorItemSelectAll({
        selectedLocations: locationStateArray,
      }),
    );
    this.store.dispatch(
      CentralLocationSelectorActions.centralLocationSelectorItemChanged(),
    );
  }

  public updateLobbyLocationSelector(locSelectorInfo: LocationSelectorInfo) {
    this.store.dispatch(
      CoreActions.updateLobbyLocationSelectorInfo({
        payload: { lobbyLocationSelectorInfo: locSelectorInfo },
      }),
    );
  }

  // Dispatch action to signout the user
  private _SignOutUser(): void {
    this.store.dispatch(
      CoreActions.userSignOut({
        payload: {
          userId: this.userId,
          locationId: this.locationId,
          reason: this.SignoutReason,
          tenantId: this.tenant?.tenantID || 0,
        },
      }),
    );
    if (!this.IsSignedIn) {
      this.session.clearSignInSession();
    }
  }

  // Hide the sigin prompt
  private _hidePopUp(): void {
    this.store.dispatch(CoreActions.closeUserSigninPrompt());
  }

  private __hideUnableToSignInPopup() {
    this.store.dispatch(CoreActions.closeUnableToSigninPopup());
  }
  private __hideUnableToCheckInPopup() {
    this.store.dispatch(CoreActions.closeUnableToCheckinPopup());
  }

  private _loadSigninDetails() {
    if (this.intialLoad && this.cachedSession != null) {
      this.store.dispatch(
        CoreActions.loadSignInDetailsForUser({
          payload: {
            userId: this.cachedSession.userId,
            tenantId: this.tenant?.tenantID || 0,
            locationId: this.cachedSession.locationId,
          },
        }),
      );
    } else if (
      this.userId !== undefined &&
      this.tenant !== undefined &&
      this.tenant.tenantID &&
      this.locationId
    ) {
      this.store.dispatch(
        CoreActions.loadSignInDetailsForUser({
          payload: {
            userId: this.userId,
            tenantId: this.tenant.tenantID,
            locationId: this.locationId,
          },
        }),
      );
    }
  }

  // -- Handlers
  public SignInPopupYesClicked(): void {
    this._SignInUser();
  }

  public SignInPopupNoClicked(): void {
    this._hidePopUp();
    this.ShowSignInPopup = false;
  }

  public CloseUnableToSignInPrompt() {
    this.__hideUnableToSignInPopup();
  }
  public CloseUnableToCheckInPrompt() {
    this.__hideUnableToCheckInPopup();
  }
  public OnActionClicked(): void {
    if (this.IsSignedIn) {
      this._SignOutUser();
    } else {
      this.subs.add(
        this._checkBranchAvailability().subscribe((status) => {
          if (status) this._ShowUserSigninPrompt();
        }),
      );
    }
  }

  // -- Handlers end

  // -- Utils

  private _checkBranchAvailability(): Observable<boolean> {
    return this.store.select(getAvailableActiveBranches).pipe(
      tap((b: LocationRelationship[]) => {
        if (b.length == 0)
          this.store.dispatch(CoreActions.promptUnableToSignin());
      }),
      switchMap((b) => of(b.length > 0)),
    );
  }

  /**
   * Fetches and sets active branches available in the location hierarchy.
   * Populates `activeBranches` and updates the UI based on the number of active branches.
   * Subscribes to various store states like lobby location, sign-in status, and user ID.
   * Calls `setCentralViewDefaultValue` to set default values for the central view.
   * FIXME Method too long
   */
  public getActiveBranchesAvailable() {
    this.locationService
      .getSelectorLocationHierachy(true, false, true, false, true)
      .subscribe({
        next: (locationsHierachy) => {
          this.locationHierachy = locationsHierachy as LocationSelector;

          this.activeBranches = this._mapActiveBranches(
            this.locationHierachy.locationsFlatData,
          );

          this._dispatchBranches(this.activeBranches);

          let defaultBranch = null;
          switch (this.activeBranches.length) {
            case 0:
              const noBranch: LocationSelectorInfo = {
                id: 0,
                locationName: "NBA",
                locationCode: "",
                dropDownDisabled: true,
                locationTypeId: 0,
                utcoffsetInMinutes: 0,
                timezoneId: "",
                timeZoneName: "",
                lastLocationAutoWraupTime: "",
              };
              this.updateLobbyLocationSelector(noBranch);
              this._showUnableToSignInPopup();
              break;
            case 1:
              defaultBranch = this._selectDefaultBranch(
                this.locationHierachy.locations[0],
              );
              const singleBranch: LocationSelectorInfo = {
                id: defaultBranch.locationId,
                locationName: defaultBranch.locationName,
                locationCode: defaultBranch.locationCode,
                dropDownDisabled: true,
                locationTypeId: 0,
                utcoffsetInMinutes: 0,
                timezoneId: defaultBranch.timeZoneId,
                timeZoneName: defaultBranch.timeZoneName,
                lastLocationAutoWraupTime:
                  defaultBranch.lastLocationAutoWraupTime,
              };
              this.updateLobbyLocationSelector(singleBranch);
              break;
            default:
              defaultBranch = this._selectDefaultBranch(
                this.locationHierachy.locations[0],
              );
              const firstAlphabetically: LocationSelectorInfo = {
                id: defaultBranch.locationId,
                locationName: defaultBranch.locationName,
                locationCode: defaultBranch.locationCode,
                dropDownDisabled: false,
                locationTypeId: 0,
                utcoffsetInMinutes: 0,
                timezoneId: defaultBranch.timeZoneId,
                timeZoneName: defaultBranch.timeZoneName,
                lastLocationAutoWraupTime:
                  defaultBranch.lastLocationAutoWraupTime,
              };
              this.updateLobbyLocationSelector(firstAlphabetically);
              break;
          }

          const locationIdSubscription = this.store
            .select(getLobbyLocationSelected)
            .subscribe((location) => {
              this._onReceiveLocationId(location);
            });

          const signinStatusSubscription = this.store
            .select(getSignedInStatus)
            .pipe(filter((status) => status != null))
            .subscribe((status: any) => {
              this._onUserSignedInStatusUpdated(status);
            });

          const userIdSubscription = this.store
            .select(getLoggedInUserId)
            .pipe(filter((id) => id != null))
            .subscribe((userId: number | undefined) => {
              if (userId) {
                this._onReceiveUserId(userId);
              }
            });

          const tenantSubscrption = this.store
            .select(getCurrentTenant)
            .subscribe((tenant: TenantModel) => {
              if (
                (!this.tenant && tenant) ||
                (this.tenant &&
                  tenant &&
                  this.tenant.tenantID !== tenant.tenantID)
              ) {
                this._onReceiveTenant(tenant);
              }
            });

          const unableToSignInPopupVisibilitySubscription = this.store
            .select(getUnableToSignInPopupPrompt)
            .subscribe(
              (flag: boolean) => (this.ShowUnableToSignInPopup = flag),
            );

          const signinPopupVisibilitySubscription = this.store
            .select(getShouldPromptUserToSignIn)
            .subscribe((flag: boolean) => {
              this._onReceiveSignInPopupPromptFlag(flag);
            });

          const unableToCheckInPopupSubscription = this.store
            .select(getUnableToCheckInPopupPrompt)
            .subscribe((flag: boolean) => {
              this.ShowUnableToCheckInPopup = flag;
            });

          this.subs.add(userIdSubscription);
          this.subs.add(locationIdSubscription);
          this.subs.add(tenantSubscrption);
          this.subs.add(signinStatusSubscription);
          this.subs.add(unableToSignInPopupVisibilitySubscription);
          this.subs.add(signinPopupVisibilitySubscription);
          this.subs.add(unableToCheckInPopupSubscription);

          this.setCentralViewDefaultValue(this.activeBranches);
        },
        error: () => {
          console.error("Error fetching Location Selector Hierarchy");
        },
      });
  }

  private _dispatchBranches(branches: LocationRelationship[]) {
    this.store.dispatch(
      CoreActions.loadAvailableActiveBranches({
        payload: { availableActiveBranches: branches },
      }),
    );
  }

  private _showUnableToSignInPopup() {
    this.store.dispatch(CoreActions.promptUnableToSignin());
  }

  // ** Utility Methods **

  // filters by branch type and maps to LocationRelationship array
  private _mapActiveBranches(
    locations: LocationSelectorItem[],
  ): LocationRelationship[] {
    return locations
      .filter((loc) => loc.locationTypeId == LocationType.Branch)
      .map(
        (loc) =>
          ({
            id: loc.locationId,
            locationName: loc.locationName,
            level: loc.locationTypeId,
          }) as LocationRelationship,
      );
  }

  // recursively goes into select the first branch of an alphabetically sorted hierarchy
  private _selectDefaultBranch(
    locSelectorItem: LocationSelectorItem,
  ): LocationSelectorItem {
    if (locSelectorItem.locationTypeId == LocationType.Branch)
      return locSelectorItem;

    if (locSelectorItem.hasChild && locSelectorItem.items) {
      for (const item of locSelectorItem.items) {
        return this._selectDefaultBranch(item);
      }
    }
    throw new Error("Unexpected code path reached!");
  }

  private _openUserAccountUpdatedPopup(
    message: string | null | undefined,
  ): void {
    this.userAccountUpdateRows = this._parseUserUpdateMessage(message ?? "");
    this.showUserAccountUpdatedPopup = this.userAccountUpdateRows.length > 0;
    if (!this.showUserAccountUpdatedPopup) {
      this._ShowUserSigninPrompt();
    }
  }

  public CloseUserAccountUpdatedPopup(): void {
    this.showUserAccountUpdatedPopup = false;
    this._ShowUserSigninPrompt();
  }

  private _parseUserUpdateMessage(
    msg: string,
  ): { fieldKey: string; value: string }[] {
    const rows: { fieldKey: string; value: string }[] = [];
    if (!msg) return rows;

    const fieldKeyMap: Record<string, string> = {
      "first name": "_General_User_First_Name_Text_",
      "last name": "_General_User_Last_Name_Text_",
      "user code": "_General_User_Code_Text_",
      "user role": "_General_User_Role_Text_",
      "home location": "_General_Home_Location_Text_",
      "primary notification email": "_General_Primary_Notification_Email_Text_",
      "opt in - daily personal summary emails":
        "_User_Appointment_Settings_Daily_Personal_Summary_Emails_Label_Text_",
      "opt in - daily location summary emails":
        "_User_Appointment_Settings_Daily_Personal_Summary_Emails_Label_Text_",
      "spanish speaker": "_User_User_Settings_Spanish_Speaker_Label_Text_",
    };

    msg
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => !!l)
      .forEach((line) => {
        const idx = line.indexOf(":");
        if (idx === -1) return;
        const rawField = line.substring(0, idx).trim();
        const value = line.substring(idx + 1).trim();
        if (!rawField || !value) return;

        const normalized = rawField.replace(/\s+/g, " ").toLowerCase();
        const fieldKey = fieldKeyMap[normalized] ?? rawField; // fallback shows raw text

        rows.push({ fieldKey, value });
      });

    return rows;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
